<template>
    <!-- Desktop Modal -->
    <UiModal
        v-if="!isMobile"
        :is-open="modelValue"
        @close="close"
        width="700px"
    >
        <template #title>
            <div
                class="flex justify-center items-center gap-2.5 flex-1 self-stretch"
            >
                <h2
                    class="flex-1 text-[#1E293B] dark:text-slate-100 text-lg sm:text-xl font-medium leading-6 sm:leading-7 m-0"
                >
                    {{
                        isSubscribed
                            ? 'Select cards to track'
                            : `Select ${maxTrackedCards} card${maxTrackedCards > 1 ? 's' : ''} to track benefits`
                    }}
                </h2>
            </div>
        </template>

        <template #content>
            <div
                class="flex flex-col items-start self-stretch gap-8 sm:gap-8 px-2 sm:px-2 pt-4 sm:pt-0 flex-1"
            >
                <!-- Upgrade Banner (only for free users) -->
                <div
                    v-if="!isSubscribed"
                    class="flex w-full py-3 px-4 items-start gap-8 rounded-lg bg-[#F1F5F9] dark:bg-slate-700"
                >
                    <!-- Image and title -->
                    <div class="flex items-center gap-3 flex-1">
                        <img
                            src="/images/freemium-banner-promostar.png"
                            alt="Upgrade"
                            class="w-6 h-6 shrink-0"
                        />
                        <span
                            class="text-[#1E293B] dark:text-slate-100 text-sm font-medium leading-5"
                        >
                            Upgrade for unlimited cards
                        </span>
                    </div>
                    <!-- Upgrade button -->
                    <button
                        type="button"
                        class="flex h-6 items-center gap-2 rounded bg-transparent border-none p-0 cursor-pointer"
                        @click="openUpgradeModal"
                    >
                        <span class="text-orange text-sm font-medium leading-5">
                            Upgrade
                        </span>
                    </button>
                </div>

                <!-- Card switch limit error callout -->
                <div
                    v-if="switchLimitError"
                    class="flex w-full py-3 px-4 items-start gap-3 rounded-lg bg-red-50 border border-red-200"
                >
                    <span class="text-red-700 text-sm font-normal leading-5">
                        {{ switchLimitError }}
                    </span>
                </div>

                <!-- Form section: search + card list -->
                <div
                    class="flex flex-col items-start gap-2 self-stretch border-b border-[#E2E8F0] dark:border-slate-700 flex-1"
                >
                    <!-- Search bar -->
                    <div class="flex flex-col items-start self-stretch">
                        <div class="relative w-full">
                            <UiInput
                                id="select-cards-search"
                                label="Search cards"
                                placeholder="Search card"
                                v-model:inputValue="searchQuery"
                                :options="{
                                    hideLabel: true,
                                    inputClass: 'text-sm',
                                    icon: 'search',
                                }"
                            />
                            <ClearInputButton
                                v-show="searchQuery"
                                @clear="clearSearch"
                                class="absolute right-2 top-1/2 -translate-y-1/2"
                            />
                        </div>
                    </div>

                    <!-- Card List: shows 4 full cards + half of 5th for scroll hint (88px per card * 4.5 = 396px) -->
                    <div
                        class="flex flex-col items-start self-stretch overflow-y-auto flex-1 sm:h-[396px] min-h-[396px] py-2 px-0.5"
                    >
                        <div
                            v-if="noCardsFound"
                            class="text-[#64748B] dark:text-slate-400 text-sm text-center py-8 w-full"
                        >
                            No cards found
                        </div>

                        <!-- Multi-user view: grouped by user with sticky headers -->
                        <template v-if="isMultiUser && cardsByUser">
                            <div
                                v-for="group in cardsByUser"
                                :key="group.userId"
                                class="w-full p-2"
                            >
                                <!-- User header (sticky) -->
                                <div
                                    class="flex items-center justify-between py-2 px-2 bg-white dark:bg-slate-800 sticky top-0 z-10 border-b border-[#E2E8F0] dark:border-slate-700"
                                >
                                    <div class="flex items-center gap-2">
                                        <Icon
                                            icon="user"
                                            class="w-4 h-4 text-slate-500"
                                        />
                                        <span
                                            class="text-sm font-medium text-[#1E293B] dark:text-slate-100"
                                        >
                                            {{ group.userName }}
                                        </span>
                                    </div>
                                    <span class="text-sm text-[#64748B] dark:text-slate-400">
                                        {{
                                            getSelectedCountForUser(
                                                group.userId
                                            )
                                        }}
                                        of {{ group.maxCards }} card
                                    </span>
                                </div>
                                <!-- User's cards -->
                                <template v-if="group.cards.length > 0">
                                    <TransitionGroup name="fade" tag="div">
                                        <button
                                            v-for="card in group.cards"
                                            :key="card.connected_account_id"
                                            type="button"
                                            class="flex w-full py-2 px-2 my-1 items-center gap-4 bg-white dark:bg-slate-800 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 rounded-lg"
                                            :class="{
                                                'ring-2 ring-slate-300 bg-slate-50 dark:bg-slate-700':
                                                    isCardSelected(card),
                                            }"
                                            @click="toggleCard(card)"
                                        >
                                            <!-- Card image -->
                                            <ImageLoader
                                                class="shrink-0 rounded overflow-hidden"
                                            >
                                                <template #image>
                                                    <img
                                                        :src="
                                                            getCardImageUrl(
                                                                card
                                                            )
                                                        "
                                                        class="h-14 w-[88px] rounded object-cover"
                                                        loading="eager"
                                                    />
                                                </template>
                                                <template #preloader>
                                                    <div
                                                        class="h-14 w-[88px] rounded bg-slate-100"
                                                    ></div>
                                                </template>
                                                <template #error>
                                                    <div
                                                        class="h-14 w-[88px] rounded bg-slate-200 flex items-center justify-center"
                                                    >
                                                        <Icon
                                                            icon="credit-card"
                                                            class="w-8 h-6 text-slate-400"
                                                        />
                                                    </div>
                                                </template>
                                            </ImageLoader>

                                            <!-- Card info -->
                                            <div
                                                class="flex-1 min-w-0 text-left"
                                            >
                                                <div
                                                    class="flex items-center gap-1"
                                                >
                                                    <span
                                                        class="text-[#1E293B] dark:text-slate-100 text-base font-semibold"
                                                    >
                                                        {{ card.name }}
                                                    </span>
                                                    <span
                                                        v-if="card.last_four"
                                                        class="text-[#64748B] dark:text-slate-400 text-base"
                                                    >
                                                        ({{ card.last_four }})
                                                    </span>
                                                </div>
                                                <div
                                                    class="text-[#64748B] dark:text-slate-400 text-sm mt-0.5"
                                                >
                                                    Up to ${{
                                                        formatValue(
                                                            card.value_up_to
                                                        )
                                                    }}/year
                                                </div>
                                            </div>

                                            <!-- Square checkbox -->
                                            <div
                                                class="shrink-0"
                                                @click.stop
                                            >
                                                <input
                                                    type="checkbox"
                                                    :checked="
                                                        isCardSelected(card)
                                                    "
                                                    @change="toggleCard(card)"
                                                    class="form-checkbox"
                                                />
                                            </div>
                                        </button>
                                    </TransitionGroup>
                                </template>
                                <!-- Empty state for users with no cards -->
                                <div
                                    v-else
                                    class="flex flex-col items-center justify-center py-6 px-4 text-center"
                                >
                                    <div
                                        class="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center mb-3"
                                    >
                                        <Icon
                                            icon="credit-card"
                                            class="w-6 h-6 text-slate-400"
                                        />
                                    </div>
                                    <p class="text-sm text-[#64748B] dark:text-slate-400 mb-3">
                                        Add {{ group.userName }}'s cards to
                                        begin tracking their benefits
                                    </p>
                                    <a
                                        href="/wallet"
                                        class="text-sm font-medium text-orange hover:text-orange-600 transition-colors"
                                    >
                                        My wallet
                                    </a>
                                </div>
                            </div>
                        </template>

                        <!-- Single user / paid view: flat list -->
                        <TransitionGroup
                            v-else
                            name="fade"
                            tag="div"
                            class="w-full"
                        >
                            <button
                                v-for="card in filteredCards"
                                :key="card.connected_account_id"
                                type="button"
                                class="flex w-full py-2 px-2 my-1 items-center gap-4 bg-white dark:bg-slate-800 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 rounded-lg"
                                :class="{
                                    'ring-2 ring-slate-300 bg-slate-50 dark:bg-slate-700':
                                        isCardSelected(card),
                                }"
                                @click="toggleCard(card)"
                            >
                                <!-- Card image -->
                                <ImageLoader
                                    class="shrink-0 rounded overflow-hidden"
                                >
                                    <template #image>
                                        <img
                                            :src="getCardImageUrl(card)"
                                            class="h-14 w-[88px] rounded object-cover"
                                            loading="eager"
                                        />
                                    </template>
                                    <template #preloader>
                                        <div
                                            class="h-14 w-[88px] rounded bg-slate-100"
                                        ></div>
                                    </template>
                                    <template #error>
                                        <div
                                            class="h-14 w-[88px] rounded bg-slate-200 flex items-center justify-center"
                                        >
                                            <Icon
                                                icon="credit-card"
                                                class="w-8 h-6 text-slate-400"
                                            />
                                        </div>
                                    </template>
                                </ImageLoader>

                                <!-- Card info -->
                                <div class="flex-1 min-w-0 text-left">
                                    <div class="flex items-center gap-1">
                                        <span
                                            class="text-[#1E293B] dark:text-slate-100 text-base font-semibold"
                                        >
                                            {{ card.name }}
                                        </span>
                                        <span
                                            v-if="card.last_four"
                                            class="text-[#64748B] dark:text-slate-400 text-base"
                                        >
                                            ({{ card.last_four }})
                                        </span>
                                    </div>
                                    <div class="text-[#64748B] dark:text-slate-400 text-sm mt-0.5">
                                        Up to ${{
                                            formatValue(card.value_up_to)
                                        }}/year
                                    </div>
                                </div>

                                <!-- Square checkbox -->
                                <div class="shrink-0" @click.stop>
                                    <input
                                        type="checkbox"
                                        :checked="isCardSelected(card)"
                                        @change="toggleCard(card)"
                                        class="form-checkbox"
                                    />
                                </div>
                            </button>
                        </TransitionGroup>
                    </div>
                </div>
            </div>
        </template>
        <template #footer>
            <div
                class="flex flex-col items-center gap-3 self-stretch pt-4 pb-12 px-6 sm:flex-row sm:items-center sm:gap-2 sm:p-6 mt-auto bg-white dark:bg-slate-800"
            >
                <!-- Counter: centered on mobile, left on desktop -->
                <div
                    class="flex items-center justify-center sm:justify-start gap-1 sm:flex-1 order-1 sm:order-none"
                >
                    <span class="text-[#64748B] dark:text-slate-400 text-sm font-normal leading-5">
                        {{ selectedCardIds.length }} of
                        {{
                            maxTrackedCards === Infinity
                                ? availableCards.length
                                : maxTrackedCards
                        }}
                        selected
                    </span>
                </div>
                <!-- Buttons: Only Save on mobile, Cancel + Save on desktop -->
                <div
                    class="flex items-center gap-2 w-full sm:w-auto order-2 sm:order-none"
                >
                    <button
                        type="button"
                        class="hidden sm:flex flex-1 sm:flex-none py-2.5 px-4 items-center justify-center gap-2 rounded border border-[#E2E8F0] dark:border-slate-700 bg-white dark:bg-slate-800 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] text-[#1E293B] dark:text-slate-100 text-sm font-medium leading-5 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                        @click="close"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        class="flex-1 sm:flex-none py-2.5 px-4 justify-center items-center gap-2 rounded bg-[#1E293B] shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] text-white text-sm font-medium leading-5 cursor-pointer hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        :disabled="saving || selectedCardIds.length === 0"
                        @click="save"
                    >
                        {{ saving ? 'Saving...' : 'Save cards' }}
                    </button>
                </div>
            </div>
        </template>
    </UiModal>

    <!-- Mobile Modal -->
    <UiModalMobile
        v-if="isMobile"
        :is-open="modelValue"
        :title="
            isSubscribed
                ? 'Select cards to track'
                : `Select ${maxTrackedCards} card${maxTrackedCards > 1 ? 's' : ''} to track benefits`
        "
        @close="close"
    >
        <template #content>
            <div class="flex flex-col items-start self-stretch gap-6 flex-1">
                <!-- Upgrade Banner (only for free users) -->
                <div
                    v-if="!isSubscribed"
                    class="flex w-full py-3 px-4 items-start gap-8 rounded-lg bg-[#F1F5F9] dark:bg-slate-700"
                >
                    <div class="flex items-center gap-3 flex-1">
                        <img
                            src="/images/freemium-banner-promostar.png"
                            alt="Upgrade"
                            class="w-6 h-6 shrink-0"
                        />
                        <span
                            class="text-[#1E293B] dark:text-slate-100 text-sm font-medium leading-5"
                        >
                            Upgrade for unlimited cards
                        </span>
                    </div>
                    <button
                        type="button"
                        class="flex h-6 items-center gap-2 rounded bg-transparent border-none p-0 cursor-pointer"
                        @click="openUpgradeModal"
                    >
                        <span class="text-orange text-sm font-medium leading-5">
                            Upgrade
                        </span>
                    </button>
                </div>

                <!-- Card switch limit error callout (mobile) -->
                <div
                    v-if="switchLimitError"
                    class="flex w-full py-3 px-4 items-start gap-3 rounded-lg bg-red-50 border border-red-200"
                >
                    <span class="text-red-700 text-sm font-normal leading-5">
                        {{ switchLimitError }}
                    </span>
                </div>

                <!-- Search bar -->
                <div class="flex flex-col items-start self-stretch">
                    <div class="relative w-full">
                        <UiInput
                            id="select-cards-search-mobile"
                            label="Search cards"
                            placeholder="Search card"
                            v-model:inputValue="searchQuery"
                            :options="{
                                hideLabel: true,
                                inputClass: 'text-sm',
                                icon: 'search',
                            }"
                        />
                        <ClearInputButton
                            v-show="searchQuery"
                            @clear="clearSearch"
                            class="absolute right-2 top-1/2 -translate-y-1/2"
                        />
                    </div>
                </div>

                <!-- Card List -->
                <div class="flex flex-col items-start self-stretch flex-1">
                    <div
                        v-if="noCardsFound"
                        class="text-[#64748B] dark:text-slate-400 text-sm text-center py-8 w-full"
                    >
                        No cards found
                    </div>

                    <!-- Multi-user view -->
                    <template v-if="isMultiUser && cardsByUser">
                        <div
                            v-for="group in cardsByUser"
                            :key="group.userId"
                            class="w-full"
                        >
                            <div
                                class="flex items-center justify-between py-2 bg-white dark:bg-slate-800 sticky top-0 z-10 border-b border-[#E2E8F0] dark:border-slate-700"
                            >
                                <div class="flex items-center gap-2">
                                    <Icon
                                        icon="user"
                                        class="w-4 h-4 text-slate-500"
                                    />
                                    <span
                                        class="text-sm font-medium text-[#1E293B] dark:text-slate-100"
                                    >
                                        {{ group.userName }}
                                    </span>
                                </div>
                                <span class="text-sm text-[#64748B] dark:text-slate-400">
                                    {{ getSelectedCountForUser(group.userId) }}
                                    of {{ group.maxCards }} card
                                </span>
                            </div>
                            <!-- User's cards -->
                            <template v-if="group.cards.length > 0">
                                <button
                                    v-for="card in group.cards"
                                    :key="card.connected_account_id"
                                    type="button"
                                    class="flex w-full py-4 items-center gap-4 border-b border-[#E2E8F0] dark:border-slate-700 last:border-b-0 bg-white dark:bg-slate-800 cursor-pointer active:bg-slate-50 dark:active:bg-slate-700"
                                    @click="toggleCard(card)"
                                >
                                    <ImageLoader
                                        class="shrink-0 rounded overflow-hidden"
                                    >
                                        <template #image>
                                            <img
                                                :src="getCardImageUrl(card)"
                                                class="h-14 w-[88px] rounded object-cover"
                                                loading="eager"
                                            />
                                        </template>
                                        <template #preloader>
                                            <div
                                                class="h-14 w-[88px] rounded bg-slate-100"
                                            ></div>
                                        </template>
                                        <template #error>
                                            <div
                                                class="h-14 w-[88px] rounded bg-slate-200 flex items-center justify-center"
                                            >
                                                <Icon
                                                    icon="credit-card"
                                                    class="w-8 h-6 text-slate-400"
                                                />
                                            </div>
                                        </template>
                                    </ImageLoader>
                                    <div class="flex-1 min-w-0 text-left">
                                        <div class="flex items-center gap-1">
                                            <span
                                                class="text-[#1E293B] dark:text-slate-100 text-base font-semibold"
                                            >
                                                {{ card.name }}
                                            </span>
                                            <span
                                                v-if="card.last_four"
                                                class="text-[#64748B] dark:text-slate-400 text-base"
                                            >
                                                ({{ card.last_four }})
                                            </span>
                                        </div>
                                        <div
                                            class="text-[#64748B] dark:text-slate-400 text-sm mt-0.5"
                                        >
                                            Up to ${{
                                                formatValue(card.value_up_to)
                                            }}/year
                                        </div>
                                    </div>
                                    <div class="shrink-0" @click.stop>
                                        <input
                                            type="radio"
                                            :checked="isCardSelected(card)"
                                            @change="toggleCard(card)"
                                            class="form-radio"
                                        />
                                    </div>
                                </button>
                            </template>
                            <!-- Empty state for users with no cards -->
                            <div
                                v-else
                                class="flex flex-col items-center justify-center py-6 px-4 text-center"
                            >
                                <div
                                    class="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center mb-3"
                                >
                                    <Icon
                                        icon="credit-card"
                                        class="w-6 h-6 text-slate-400"
                                    />
                                </div>
                                <p class="text-sm text-[#64748B] dark:text-slate-400 mb-3">
                                    No cards in wallet
                                </p>
                                <a
                                    href="/wallet"
                                    class="text-sm font-medium text-orange hover:text-orange-600 transition-colors"
                                >
                                    Add cards to wallet
                                </a>
                            </div>
                        </div>
                    </template>

                    <!-- Single user / paid view -->
                    <template v-else>
                        <button
                            v-for="card in filteredCards"
                            :key="card.connected_account_id"
                            type="button"
                            class="flex w-full py-4 items-center gap-4 border-b border-[#E2E8F0] dark:border-slate-700 last:border-b-0 bg-white dark:bg-slate-800 cursor-pointer active:bg-slate-50 dark:active:bg-slate-700"
                            @click="toggleCard(card)"
                        >
                            <ImageLoader
                                class="shrink-0 rounded overflow-hidden"
                            >
                                <template #image>
                                    <img
                                        :src="getCardImageUrl(card)"
                                        class="h-14 w-[88px] rounded object-cover"
                                        loading="eager"
                                    />
                                </template>
                                <template #preloader>
                                    <div
                                        class="h-14 w-[88px] rounded bg-slate-100"
                                    ></div>
                                </template>
                                <template #error>
                                    <div
                                        class="h-14 w-[88px] rounded bg-slate-200 flex items-center justify-center"
                                    >
                                        <Icon
                                            icon="credit-card"
                                            class="w-8 h-6 text-slate-400"
                                        />
                                    </div>
                                </template>
                            </ImageLoader>
                            <div class="flex-1 min-w-0 text-left">
                                <div class="flex items-center gap-1">
                                    <span
                                        class="text-[#1E293B] dark:text-slate-100 text-base font-semibold"
                                    >
                                        {{ card.name }}
                                    </span>
                                    <span
                                        v-if="card.last_four"
                                        class="text-[#64748B] dark:text-slate-400 text-base"
                                    >
                                        ({{ card.last_four }})
                                    </span>
                                </div>
                                <div class="text-[#64748B] dark:text-slate-400 text-sm mt-0.5">
                                    Up to ${{
                                        formatValue(card.value_up_to)
                                    }}/year
                                </div>
                            </div>
                            <div class="shrink-0" @click.stop>
                                <input
                                    type="radio"
                                    :checked="isCardSelected(card)"
                                    @change="toggleCard(card)"
                                    class="form-radio"
                                />
                            </div>
                        </button>
                    </template>
                </div>
            </div>
        </template>
        <template #footer>
            <div class="flex flex-col items-center gap-3 self-stretch">
                <span class="text-[#64748B] dark:text-slate-400 text-sm font-normal leading-5">
                    {{ selectedCardIds.length }} of
                    {{
                        maxTrackedCards === Infinity
                            ? availableCards.length
                            : maxTrackedCards
                    }}
                    selected
                </span>
                <button
                    type="button"
                    class="w-full py-2.5 px-4 justify-center items-center gap-2 rounded bg-[#1E293B] shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] text-white text-sm font-medium leading-5 cursor-pointer active:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    :disabled="saving || selectedCardIds.length === 0"
                    @click="save"
                >
                    {{ saving ? 'Saving...' : 'Save cards' }}
                </button>
            </div>
        </template>
    </UiModalMobile>

    <!-- Upgrade Modal -->
    <UpgradeModal :show="showUpgradeModal" @close="showUpgradeModal = false" />
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useCardBenefitsStore } from '@/store/card-benefits';
import { useIsMobile } from '@/composables/useIsMobile';
import Icon from '@/components/ui/icon/icon.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import UiModal from '@/components/ui/modal/ui-modal.vue';
import UiModalMobile from '@/components/ui/modal/ui-modal-mobile.vue';
import UiInput from '@/components/ui/inputs/ui-input.vue';
import ClearInputButton from '@/components/ui/clear-input-button/clear-input-button.vue';
import UpgradeModal from '@/modals/upgrade-modal.vue';
const { isMobile } = useIsMobile();

const props = defineProps({
    modelValue: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['update:modelValue']);

const store = useCardBenefitsStore();
const {
    isSubscribed,
    maxTrackedCards,
    availableCardsForTracking,
    walletUsersCount,
    walletUsers,
    freemium,
} = storeToRefs(store);

const searchQuery = ref('');
const selectedCardIds = ref([]);
const saving = ref(false);
const showUpgradeModal = ref(false);
const switchLimitError = ref(null);

const cdnUrl = computed(() => window.cdnUrl);

const availableCards = computed(() => availableCardsForTracking.value);

// Check if multi-user mode (show grouped view for free users with multiple users)
const isMultiUser = computed(() => {
    return !isSubscribed.value && walletUsersCount.value > 1;
});

// Group cards by user for multi-user free accounts
const cardsByUser = computed(() => {
    if (!isMultiUser.value) {
        return null;
    }

    const cards = searchQuery.value
        ? availableCards.value.filter(
              (card) =>
                  card.name
                      .toLowerCase()
                      .includes(searchQuery.value.toLowerCase()) ||
                  (card.last_four && card.last_four.includes(searchQuery.value))
          )
        : availableCards.value;

    // Start with all wallet users to ensure users with no cards are included
    const groups = {};
    (walletUsers.value || []).forEach((user) => {
        groups[user.id] = {
            userId: user.id,
            userName: user.name,
            cards: [],
            trackedCount: 0,
            maxCards: 1, // 1 card per user on free plan
        };
    });

    // Add cards to their respective user groups
    cards.forEach((card) => {
        const userId = card.user_id;
        if (userId && groups[userId]) {
            groups[userId].cards.push(card);
            if (
                card.is_tracked ||
                selectedCardIds.value.includes(card.connected_account_id)
            ) {
                groups[userId].trackedCount++;
            }
        }
    });

    return Object.values(groups);
});

// Check if no cards found (handles both multi-user grouped view and flat list)
const noCardsFound = computed(() => {
    if (isMultiUser.value && cardsByUser.value) {
        return cardsByUser.value.every((group) => group.cards.length === 0);
    }
    return filteredCards.value.length === 0;
});

// Filter cards by search query (flat list for single user or paid)
const filteredCards = computed(() => {
    if (!searchQuery.value) {
        return availableCards.value;
    }

    const query = searchQuery.value.toLowerCase();
    return availableCards.value.filter(
        (card) =>
            card.name.toLowerCase().includes(query) ||
            (card.last_four && card.last_four.includes(query))
    );
});

const getCardImageUrl = (card) => {
    if (card.image?.path && card.image?.file_name) {
        return `${cdnUrl.value}/${card.image.path}/_small/${card.image.file_name}`;
    }
    return null;
};

const isCardSelected = (card) => {
    return selectedCardIds.value.includes(card.connected_account_id);
};

// Get count of selected cards per user
const getSelectedCountForUser = (userId) => {
    return availableCards.value.filter(
        (c) =>
            c.user_id === userId &&
            selectedCardIds.value.includes(c.connected_account_id)
    ).length;
};

const toggleCard = (card) => {
    const id = card.connected_account_id;
    const index = selectedCardIds.value.indexOf(id);

    if (index > -1) {
        // Deselect
        selectedCardIds.value.splice(index, 1);
    } else {
        if (isSubscribed.value) {
            // Paid: multi-select with checkbox
            selectedCardIds.value.push(id);
        } else if (isMultiUser.value) {
            // Free multi-user: 1 card per user
            const userSelectedCount = getSelectedCountForUser(card.user_id);
            if (userSelectedCount >= 1) {
                // Replace the user's current selection
                const userCardIds = availableCards.value
                    .filter((c) => c.user_id === card.user_id)
                    .map((c) => c.connected_account_id);
                selectedCardIds.value = selectedCardIds.value.filter(
                    (id) => !userCardIds.includes(id)
                );
                selectedCardIds.value.push(id);
            } else {
                selectedCardIds.value.push(id);
            }
        } else {
            // Free single user: 1 card total
            if (selectedCardIds.value.length >= maxTrackedCards.value) {
                selectedCardIds.value = [id];
            } else {
                selectedCardIds.value.push(id);
            }
        }
    }
};

const formatValue = (value) => {
    if (!value) return '0';
    return Math.round(value).toLocaleString();
};

const clearSearch = () => {
    searchQuery.value = '';
};

const close = () => {
    emit('update:modelValue', false);
    store.setShowSelectCardsModal(false);
    searchQuery.value = '';
};

const openUpgradeModal = () => {
    showUpgradeModal.value = true;
};

const switchLimitReached = computed(() => {
    if (isSubscribed.value) return false;
    return (
        freemium.value.monthlySwitchesUsed >= freemium.value.maxMonthlySwitches
    );
});

const save = async () => {
    if (selectedCardIds.value.length === 0) return;

    saving.value = true;
    switchLimitError.value = null;

    const result = await store.saveTrackedCards(selectedCardIds.value);

    saving.value = false;

    if (result.success) {
        // Modal is closed by the store action, just reset search
        searchQuery.value = '';
        switchLimitError.value = null;
    } else if (result.switchLimitReached) {
        switchLimitError.value = result.error;
    } else if (result.requiresUpgrade) {
        showUpgradeModal.value = true;
    }
};

// Initialize selected cards when modal opens
watch(
    () => props.modelValue,
    (isOpen) => {
        if (isOpen) {
            // Pre-select currently tracked cards
            selectedCardIds.value = availableCards.value
                .filter((card) => card.is_tracked)
                .map((card) => card.connected_account_id);
            switchLimitError.value = null;
        } else {
            // Reset search when modal closes
            searchQuery.value = '';
            switchLimitError.value = null;
        }
    }
);
</script>
